import { Icon } from '../common';
import type { MeToday, MeTodayFoodDog, MeTodayLeavingDog, SelfViewWeek, StaffTab } from '@bbbk/schema';
import { heroDate, leavesLabel, nextShiftLabel, weekdayLong } from './todayFormat';

// Which day this panel is briefing — drives the hero eyebrow, the off-state
// copy and the Leaving card title/empty. The date itself is already the right
// day (the server hands the target date on each payload), so heroDate needs no
// change; only the words do. 'day-after' (staff-daily-lookahead) is named by
// its date — the eyebrow is the weekday, the copy says "that day". Derived
// from StaffTab so a new briefing day is ONE schema edit, not two unions.
export type BriefWhen = Exclude<StaffTab, 'shifts' | 'info'>;

// The staff Today briefing (staff-food-at-house): a day hero above a stack of
// briefing cards — Food at the house first, Medicine second, each mirroring
// the other's states (populated / designed empty / skeleton / error+retry).
// Future day-cards join the stack as siblings. Presentational: the fetch
// lives in StaffView so the Today-tab count badge reads the same load.

export type TodayLoad =
  | { state: 'loading' }
  | { state: 'error'; retry: () => void }
  | { state: 'ready'; data: MeToday };

// The little dog face beside each name — the app's standard paw, bare and
// muted so the card header stays the one visual anchor. `children` stack under
// the name inside the (column-flex) name cell — the Leaving card's tag row.
function DogName({ name, breed, children }: { name: string; breed: string | null; children?: React.ReactNode }) {
  return (
    <>
      <span className="staff-food-avatar" aria-hidden="true">
        <Icon name="paw" size={15} />
      </span>
      <span className="staff-food-name">
        <span>
          {name}
          {breed ? <span className="staff-food-breed">{breed}</span> : null}
        </span>
        {children}
      </span>
    </>
  );
}

// The indicator chips on a leaving dog, as data. ARRAY ORDER is the render
// order — wash FIRST (the one job that can't wait), then food, meds,
// keep-apart. Each chip shows only when its flag is set; a dog with no flags
// renders nothing. `flag` names the MeTodayLeavingDog boolean, `tone` is the
// CSS variant whose hue mirrors that briefing card's icon (wash = calm mint,
// never an alarm colour). Add a tag = add a row.
const LEAVING_TAGS: ReadonlyArray<{
  flag: "wash" | "food" | "meds" | "keepApart";
  tone: string;
  icon: string;
  label: string;
}> = [
  { flag: "wash", tone: "wash", icon: "droplet", label: "Please wash" },
  { flag: "food", tone: "food", icon: "bowl", label: "Food" },
  { flag: "meds", tone: "meds", icon: "medication", label: "Meds" },
  { flag: "keepApart", tone: "mix", icon: "do-not-mix", label: "Keep apart" },
];

function LeavingTags({ dog }: { dog: MeTodayLeavingDog }) {
  const chips = LEAVING_TAGS.filter((t) => dog[t.flag]).map((t) => (
    <span key={t.tone} className={`tag ${t.tone}`}>
      <Icon name={t.icon} size={12} />
      {t.label}
    </span>
  ));
  return chips.length ? <span className="tags">{chips}</span> : null;
}

function LeavesPill({ leavesOn, today }: { leavesOn: string; today: string }) {
  return (
    <span className={'staff-food-until' + (leavesOn === today ? ' last-day' : '')}>
      {leavesLabel(leavesOn, today)}
    </span>
  );
}

function Skeleton({ rows }: { rows: number }) {
  return (
    <div aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skel">
          <span className="b" style={{ width: i % 2 ? '30%' : '38%' }} />
          <span className="b" style={{ width: '22%', marginLeft: 'auto' }} />
        </div>
      ))}
    </div>
  );
}

// One briefing card, generic over its row type. The states mirror across every
// card — same anatomy, different icon/copy/rows (Freddi, prototype review
// 2026-07-16). The Leaving card is the same card with `MeTodayLeavingDog` rows
// and the `leaving` modifier; food/meds/keep-apart carry `MeTodayFoodDog`. The
// generic keeps `renderRow` honestly typed — no `any`, no `as never`.
function BriefCard<T extends { id: string }>({
  load,
  icon,
  iconClass,
  cardClass,
  title,
  dayWord = 'today',
  emptyCopy,
  footCopy,
  rows,
  renderRow,
}: {
  load: TodayLoad;
  icon: string;
  iconClass?: string;
  cardClass?: string;
  title: string;
  dayWord?: string; // 'today' | 'tomorrow' | 'that day' — day-aware error copy
  emptyCopy: string;
  footCopy: string;
  rows: T[];
  renderRow: (dog: T, today: string) => React.ReactNode;
}) {
  const ready = load.state === 'ready' ? load.data : null;
  return (
    <section className={'brief-card' + (cardClass ? ` ${cardClass}` : '')}>
      <div className="brief-head">
        <span className={'brief-icon' + (iconClass ? ` ${iconClass}` : '')} aria-hidden="true">
          <Icon name={icon} size={16} />
        </span>
        <h2 className="brief-title">{title}</h2>
        {ready && rows.length > 0 && (
          <span className="brief-count">{rows.length} {rows.length === 1 ? 'dog' : 'dogs'}</span>
        )}
      </div>
      {load.state === 'loading' && <Skeleton rows={2} />}
      {load.state === 'error' && (
        <div className="brief-error">
          Couldn't load {dayWord}'s list.
          <button className="brief-retry" type="button" onClick={load.retry}>Retry</button>
        </div>
      )}
      {ready && rows.length === 0 && (
        <div className="brief-empty">
          <span className={'brief-icon' + (iconClass ? ` ${iconClass}` : '')} aria-hidden="true">
            <Icon name={icon} size={16} />
          </span>
          <span>{emptyCopy}</span>
        </div>
      )}
      {ready && rows.length > 0 && (
        <>
          <ul className="staff-food-list">
            {rows.map((dog) => (
              <li key={dog.id} className="staff-food-dog">
                {renderRow(dog, ready.date)}
              </li>
            ))}
          </ul>
          <div className="brief-foot">{footCopy}</div>
        </>
      )}
    </section>
  );
}

export function TodayPanel({ load, weeks, when = 'today' }: { load: TodayLoad; weeks: SelfViewWeek[]; when?: BriefWhen }) {
  const ready = load.state === 'ready' ? load.data : null;
  const off = !!ready && !ready.rostered;
  const next = off ? nextShiftLabel(weeks, ready!.date) : null;
  // "Today" / "Tomorrow" / the weekday for the eyebrow; the lower-case word
  // for the copy — 'day-after' reads day-neutral ("that day").
  const dayLabel =
    when === 'today' ? 'Today' : when === 'tomorrow' ? 'Tomorrow' : ready ? weekdayLong(ready.date) : '…';
  const dayWord = when === 'today' ? 'today' : when === 'tomorrow' ? 'tomorrow' : 'that day';

  return (
    <div className="staff-panel">
      <section className={'day-hero' + (off ? ' off' : '')} aria-label={dayLabel}>
        <div className="day-hero-left">
          <span className="day-hero-eyebrow">{dayLabel}</span>
          <span className="day-hero-date">{ready ? heroDate(ready.date) : '…'}</span>
          {off && (
            <span className="day-hero-sub">
              Not working {dayWord}{next ? <> · next shift <strong>{next}</strong></> : null}
            </span>
          )}
        </div>
        {/* Badges only when the day carries the caller's own shifts — a
            look-ahead day they're off is visible but badge-less. */}
        {!off && ready && ready.shifts.length > 0 && (
          <div className="shift-badges">
            {ready.shifts.includes('am') && <span className="shift-badge am">AM</span>}
            {ready.shifts.includes('pm') && <span className="shift-badge pm">PM</span>}
          </div>
        )}
      </section>

      {/* Briefing cards exist on working days (and while that's undecided —
          loading/error); a not-working day shows the quiet hero alone. */}
      {!off && (
        <>
          {/* The Leaving card leads the stack — a dog going home today shows
              ONLY here, its food/meds/keep-apart riding along as tags, so it
              never repeats in the three lists below (dedup is server-side). */}
          <BriefCard<MeTodayLeavingDog>
            load={load}
            icon="log-out"
            cardClass="leaving"
            dayWord={dayWord}
            title={`Leaving ${dayWord}`}
            emptyCopy={`No dogs leaving ${dayWord}.`}
            footCopy="Leaving dogs show here only — their food, meds and keep-apart appear as tags."
            rows={ready ? ready.leavingDogs : []}
            renderRow={(dog) => (
              <DogName name={dog.name} breed={dog.breed}>
                <LeavingTags dog={dog} />
              </DogName>
            )}
          />
          <BriefCard<MeTodayFoodDog>
            load={load}
            icon="bowl"
            dayWord={dayWord}
            title="Food at the house"
            emptyCopy={`No house food ${dayWord} — every dog in is on kennel food. Nothing to collect.`}
            footCopy="Their food is stored at the house — collect before feeds, top up if low."
            rows={ready ? ready.foodDogs : []}
            renderRow={(dog: MeTodayFoodDog, today) => (
              <>
                <DogName name={dog.name} breed={dog.breed} />
                <LeavesPill leavesOn={dog.leavesOn} today={today} />
              </>
            )}
          />
          <BriefCard<MeTodayFoodDog>
            load={load}
            icon="medication"
            iconClass="meds"
            dayWord={dayWord}
            title="Medicine"
            emptyCopy={`No medicine ${dayWord} — no dog in is marked for meds. Nothing to give.`}
            footCopy="Give meds as the note says — and keep a close eye on how they're doing."
            rows={ready ? ready.medsDogs : []}
            renderRow={(dog: MeTodayFoodDog, today) => (
              <>
                <DogName name={dog.name} breed={dog.breed} />
                <LeavesPill leavesOn={dog.leavesOn} today={today} />
              </>
            )}
          />
          <BriefCard<MeTodayFoodDog>
            load={load}
            icon="do-not-mix"
            iconClass="mix"
            dayWord={dayWord}
            title="Keep apart"
            emptyCopy={`No keep-aparts ${dayWord} — every dog in can mix.`}
            footCopy="Keep them apart from other households' dogs — separate runs and turns."
            rows={ready ? ready.keepApartDogs : []}
            renderRow={(dog: MeTodayFoodDog, today) => (
              <>
                <DogName name={dog.name} breed={dog.breed} />
                <LeavesPill leavesOn={dog.leavesOn} today={today} />
              </>
            )}
          />
        </>
      )}
    </div>
  );
}
